// Shared phone number helpers built on libphonenumber-js.
//
// Used by the Profile page and the sign-up form so phone entry, country
// selection, and validation stay consistent. Numbers are normalized to E.164
// (e.g. +15551234567) before they are sent to the backend, which re-validates
// them. The "everything needed for SMS" lives in that single E.164 string.

import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

export const DEFAULT_PHONE_COUNTRY = "US";

function countryName(code) {
  try {
    const dn = new Intl.DisplayNames(["en"], { type: "region" });
    return dn.of(code) || code;
  } catch {
    return code;
  }
}

// Build a sorted list of { code, label } for the country <select>, e.g.
// { code: "US", label: "United States (+1)" }.
export const PHONE_COUNTRY_OPTIONS = getCountries()
  .map((code) => {
    let calling = "";
    try {
      calling = getCountryCallingCode(code);
    } catch {
      calling = "";
    }
    return {
      code,
      name: countryName(code),
      label: `${countryName(code)} (+${calling})`,
    };
  })
  .sort((a, b) => a.name.localeCompare(b.name));

// Format the user's input as they type, scoped to the selected country.
export function formatAsYouType(value, country) {
  if (!value) return "";
  try {
    return new AsYouType(country || DEFAULT_PHONE_COUNTRY).input(value);
  } catch {
    return value;
  }
}

// Validate a (possibly empty) phone entry for the given country.
// Empty is valid (the field is optional) and yields e164 = null.
// Returns { valid, e164, error }.
export function validatePhone(value, country) {
  const trimmed = (value || "").trim();
  if (!trimmed) return { valid: true, e164: null, error: "" };
  try {
    const parsed = parsePhoneNumberFromString(trimmed, country || DEFAULT_PHONE_COUNTRY);
    if (parsed && parsed.isValid()) {
      return { valid: true, e164: parsed.number, error: "" };
    }
  } catch {
    // fall through to invalid
  }
  return { valid: false, e164: null, error: "Enter a valid phone number." };
}

// Given a stored E.164 number, figure out which country to preselect.
export function deriveCountryFromE164(stored) {
  if (!stored) return DEFAULT_PHONE_COUNTRY;
  try {
    const parsed = parsePhoneNumberFromString(stored);
    return parsed?.country || DEFAULT_PHONE_COUNTRY;
  } catch {
    return DEFAULT_PHONE_COUNTRY;
  }
}

// Format a stored E.164 number for display in the input (national format for
// the parsed country); falls back to the raw value if it can't be parsed.
export function formatStoredForDisplay(stored) {
  if (!stored) return "";
  try {
    const parsed = parsePhoneNumberFromString(stored);
    if (parsed) return parsed.formatNational();
  } catch {
    // fall through
  }
  return stored;
}
